import './SitesFilters.css';
import { useState, useMemo } from 'react';
import { HiOutlineX, HiOutlineFilter } from 'react-icons/hi';
import { HiOutlineUserGroup, HiOutlineMapPin, HiOutlineShieldCheck, HiOutlineChartBar, HiOutlineSun, HiOutlineBolt, HiOutlineTag, HiOutlineCurrencyDollar } from 'react-icons/hi2';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import MultiSelectDropdown from '../MultiSelectDropdown/MultiSelectDropdown';
import RangeSlider from '../RangeSlider/RangeSlider';
import { getTooltipText } from '../../utils/tooltip';

interface StoreGeoEntry {
  state: string;
  county: string;
  city: string;
}

interface SitesFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters?: (filters: SitesFilterParams) => void;
  availableStates?: string[];
  availableCounties?: string[];
  availableCities?: string[];
  storeGeoData?: StoreGeoEntry[];
  isUltimateParent?: boolean;
  availableSubBrands?: string[];
}

export interface SitesFilterParams {
  status?: string;
  solutions?: string | string[];
  whitespace?: string[];
  crimeLevel?: string[];
  cartPenalty?: string;
  quickFilters?: string[];
  purchekNeighborhoodSites?: { min: number; max: number };
  purchekPenetration?: { min: number; max: number };
  hotDays?: { min: number; max: number };
  coldDays?: { min: number; max: number };
  locatedInShoppingComplex?: string;
  pairedWheelPenetration?: { min: number; max: number };
  singleWheelReplacement?: { min: number; max: number };
  containmentNeighborhoodSites?: { min: number; max: number };
  containmentPenetration?: { min: number; max: number };
  subBrands?: string[];
  states?: string[];
  counties?: string[];
  cities?: string[];
  neighbourhoodPushoutIncidents?: string[];
  storePushoutIncidents?: string[];
  neighbourhoodRecoveryAmount?: string[];
  storeRecoveryAmount?: string[];
  storeDensity?: string[];
  highwayProximity?: string[];
}

function SitesFilters({ isOpen, onClose, onApplyFilters, availableStates = [], availableCounties = [], availableCities = [], storeGeoData = [], isUltimateParent, availableSubBrands = [] }: SitesFiltersProps) {
  const [status, setStatus] = useState('All Status');
  const [solutions, setSolutions] = useState<string[]>([]);
  const [whitespace, setWhitespace] = useState<string[]>([]);
  const [crimeLevel, setCrimeLevel] = useState<string[]>([]);
  const [cartPenalty, setCartPenalty] = useState('All');
  const [quickFilters, setQuickFilters] = useState<string[]>([]);

  const [states, setStates] = useState<string[]>([]);
  const [counties, setCounties] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  // Dependent filtering: counties filtered by selected states, cities filtered by selected states + counties
  const filteredCounties = useMemo(() => {
    if (states.length === 0) return availableCounties;
    const countySet = new Set<string>();
    storeGeoData.forEach(s => {
      if (states.includes(s.state) && s.county !== '-' && s.county !== '') {
        countySet.add(s.county);
      }
    });
    return Array.from(countySet).sort((a, b) => a.localeCompare(b));
  }, [states, storeGeoData, availableCounties]);

  const filteredCities = useMemo(() => {
    if (states.length === 0 && counties.length === 0) return availableCities;
    const citySet = new Set<string>();
    storeGeoData.forEach(s => {
      const matchState = states.length === 0 || states.includes(s.state);
      const matchCounty = counties.length === 0 || counties.includes(s.county);
      if (matchState && matchCounty && s.city !== '-' && s.city !== '') {
        citySet.add(s.city);
      }
    });
    return Array.from(citySet).sort((a, b) => a.localeCompare(b));
  }, [states, counties, storeGeoData, availableCities]);

  const [purchekNeighborhoodSites, setPurchekNeighborhoodSites] = useState({ min: 0, max: 100 });
  const [purchekPenetration, setPurchekPenetration] = useState({ min: 0, max: 100 });
  const [containmentNeighborhoodSites, setContainmentNeighborhoodSites] = useState({ min: 0, max: 100 });
  const [containmentPenetration, setContainmentPenetration] = useState({ min: 0, max: 100 });
  const [pairedWheelPenetration, setPairedWheelPenetration] = useState({ min: 0, max: 100 });
  const [singleWheelReplacement, setSingleWheelReplacement] = useState({ min: 0, max: 100 });
  const [hotDays, setHotDays] = useState({ min: 0, max: 365 });
  const [coldDays, setColdDays] = useState({ min: 0, max: 365 });
  const [locatedInShoppingComplex, setLocatedInShoppingComplex] = useState('All');
  const [subBrands, setSubBrands] = useState<string[]>([]);
  const [neighbourhoodPushoutIncidents, setNeighbourhoodPushoutIncidents] = useState<string[]>([]);
  const [storePushoutIncidents, setStorePushoutIncidents] = useState<string[]>([]);
  const [neighbourhoodRecoveryAmount, setNeighbourhoodRecoveryAmount] = useState<string[]>([]);
  const [storeRecoveryAmount, setStoreRecoveryAmount] = useState<string[]>([]);
  const [storeDensity, setStoreDensity] = useState<string[]>([]);
  const [highwayProximity, setHighwayProximity] = useState<string[]>([]);

  const statusOptions = ['All Status', 'Customer', 'Non-Customer'];
  const solutionsOptions = ['Purchek', 'Cart Containment', 'Cart Manager', 'Subscription', 'Paired Wheel'];
  const whitespaceOptions = ['Purchek', 'Cart Containment', 'Cart Manager', 'Subscription', 'Paired Wheel'];
  const crimeLevelOptions = ['Low', 'Medium', 'High', 'Critical'];
  const cartAbandonmentOptions = ['All', 'No Ordinance', 'General Recommendation', 'Penalty for Cart Owner'];
  const neighbourhoodPushoutOptions = ['0-250', '250-500', '500-1000', '1000-2000', '2000-5000', '5000+'];
  const storePushoutOptions = ['0-250', '250-500', '500-1000', '1000+'];
  const neighbourhoodRecoveryOptions = ['$0-500', '$500-$1000', '$1000-$2000', '$2000-$5000', '$5000-$10000', '$10000-$20000', '$20000-$50000', '$50000-$100000', '$100000+'];
  const storeRecoveryOptions = ['$0-500', '$500-$1000', '$1000-$2000', '$2000-$5000', '$5000-$10000', '$10000+'];
  const storeDensityOptions = ['None', 'Low', 'Medium', 'High'];
  const highwayProximityOptions = ['Very Close', 'Near', 'Moderate', 'Far'];

  const quickFilterOptions = [
    { id: 'high-crime', label: 'High Crime Sites' },
    { id: 'cart-penalty', label: 'Cart Ordinance Required' },
    { id: 'any-opportunity', label: 'Any Opportunity' },
    { id: 'dormant-stores', label: 'Out of Touch Stores' },
  ];

  const isRangeActive = (range: { min: number; max: number }, maxLimit: number) =>
    range.min > 0 || range.max < maxLimit;

  const activeFilterCount =
    (status !== 'All Status' ? 1 : 0) +
    (solutions.length > 0 ? 1 : 0) +
    (cartPenalty !== 'All' ? 1 : 0) +
    quickFilters.length +
    whitespace.length +
    crimeLevel.length +
    (states.length > 0 ? 1 : 0) +
    (counties.length > 0 ? 1 : 0) +
    (cities.length > 0 ? 1 : 0) +
    (isRangeActive(purchekNeighborhoodSites, 100) ? 1 : 0) +
    (isRangeActive(purchekPenetration, 100) ? 1 : 0) +
    (isRangeActive(containmentNeighborhoodSites, 100) ? 1 : 0) +
    (isRangeActive(containmentPenetration, 100) ? 1 : 0) +
    (isRangeActive(pairedWheelPenetration, 100) ? 1 : 0) +
    (isRangeActive(singleWheelReplacement, 100) ? 1 : 0) +
    (isRangeActive(hotDays, 365) ? 1 : 0) +
    (isRangeActive(coldDays, 365) ? 1 : 0) +
    (locatedInShoppingComplex !== 'All' ? 1 : 0) +
    (subBrands.length > 0 ? 1 : 0) +
    (neighbourhoodPushoutIncidents.length > 0 ? 1 : 0) +
    (storePushoutIncidents.length > 0 ? 1 : 0) +
    (neighbourhoodRecoveryAmount.length > 0 ? 1 : 0) +
    (storeRecoveryAmount.length > 0 ? 1 : 0) +
    (storeDensity.length > 0 ? 1 : 0) +
    (highwayProximity.length > 0 ? 1 : 0);

  const getGroupFilterCount = (counts: boolean[]) => counts.filter(Boolean).length;

  const storeStatusCount = getGroupFilterCount([
    status !== 'All Status',
    solutions.length > 0,
    whitespace.length > 0,
  ]);

  const locationCount = getGroupFilterCount([
    states.length > 0,
    counties.length > 0,
    cities.length > 0,
    locatedInShoppingComplex !== 'All',
    storeDensity.length > 0,
    highwayProximity.length > 0,
  ]);

  const riskCount = getGroupFilterCount([
    crimeLevel.length > 0,
    cartPenalty !== 'All',
  ]);

  const gkapCount = getGroupFilterCount([
    isRangeActive(purchekNeighborhoodSites, 100),
    isRangeActive(purchekPenetration, 100),
    isRangeActive(containmentNeighborhoodSites, 100),
    isRangeActive(containmentPenetration, 100),
    isRangeActive(pairedWheelPenetration, 100),
    isRangeActive(singleWheelReplacement, 100),
  ]);

  const environmentalCount = getGroupFilterCount([
    isRangeActive(hotDays, 365),
    isRangeActive(coldDays, 365),
  ]);

  const pushoutRecoveryCount = getGroupFilterCount([
    neighbourhoodPushoutIncidents.length > 0,
    storePushoutIncidents.length > 0,
    neighbourhoodRecoveryAmount.length > 0,
    storeRecoveryAmount.length > 0,
  ]);

  const handleQuickFilterToggle = (filterId: string) => {
    setQuickFilters((prev) =>
      prev.includes(filterId) ? prev.filter((id) => id !== filterId) : [...prev, filterId]
    );
  };

  // Cascade handlers: changing state clears county & city, changing county clears city
  const handleStatesChange = (newStates: string[]) => {
    setStates(newStates);
    setCounties([]);
    setCities([]);
  };

  const handleCountiesChange = (newCounties: string[]) => {
    setCounties(newCounties);
    setCities([]);
  };

  const handleClearAllFilters = () => {
    setStatus('All Status');
    setSolutions([]);
    setWhitespace([]);
    setCrimeLevel([]);
    setCartPenalty('All');
    setQuickFilters([]);
    setStates([]);
    setCounties([]);
    setCities([]);
    setPurchekNeighborhoodSites({ min: 0, max: 100 });
    setPurchekPenetration({ min: 0, max: 100 });
    setContainmentNeighborhoodSites({ min: 0, max: 100 });
    setContainmentPenetration({ min: 0, max: 100 });
    setPairedWheelPenetration({ min: 0, max: 100 });
    setSingleWheelReplacement({ min: 0, max: 100 });
    setHotDays({ min: 0, max: 365 });
    setColdDays({ min: 0, max: 365 });
    setLocatedInShoppingComplex('All');
    setSubBrands([]);
    setNeighbourhoodPushoutIncidents([]);
    setStorePushoutIncidents([]);
    setNeighbourhoodRecoveryAmount([]);
    setStoreRecoveryAmount([]);
    setStoreDensity([]);
    setHighwayProximity([]);
  };

  const handleApplyFilters = () => {
    const appliedFilters: SitesFilterParams = {
      status: status !== 'All Status' ? status : undefined,
      solutions: solutions.length > 0 ? solutions.join(',') : undefined,
      whitespace: whitespace.length > 0 ? whitespace : undefined,
      crimeLevel: crimeLevel.length > 0 ? crimeLevel : undefined,
      cartPenalty: cartPenalty !== 'All' ? cartPenalty : undefined,
      quickFilters: quickFilters.length > 0 ? quickFilters : undefined,
      states: states.length > 0 ? states : undefined,
      counties: counties.length > 0 ? counties : undefined,
      cities: cities.length > 0 ? cities : undefined,
      purchekNeighborhoodSites: isRangeActive(purchekNeighborhoodSites, 100) ? purchekNeighborhoodSites : undefined,
      purchekPenetration: isRangeActive(purchekPenetration, 100) ? purchekPenetration : undefined,
      containmentNeighborhoodSites: isRangeActive(containmentNeighborhoodSites, 100) ? containmentNeighborhoodSites : undefined,
      containmentPenetration: isRangeActive(containmentPenetration, 100) ? containmentPenetration : undefined,
      pairedWheelPenetration: isRangeActive(pairedWheelPenetration, 100) ? pairedWheelPenetration : undefined,
      singleWheelReplacement: isRangeActive(singleWheelReplacement, 100) ? singleWheelReplacement : undefined,
      hotDays: isRangeActive(hotDays, 365) ? hotDays : undefined,
      coldDays: isRangeActive(coldDays, 365) ? coldDays : undefined,
      locatedInShoppingComplex: locatedInShoppingComplex !== 'All' ? locatedInShoppingComplex : undefined,
      subBrands: subBrands.length > 0 ? subBrands : undefined,
      neighbourhoodPushoutIncidents: neighbourhoodPushoutIncidents.length > 0 ? neighbourhoodPushoutIncidents : undefined,
      storePushoutIncidents: storePushoutIncidents.length > 0 ? storePushoutIncidents : undefined,
      neighbourhoodRecoveryAmount: neighbourhoodRecoveryAmount.length > 0 ? neighbourhoodRecoveryAmount : undefined,
      storeRecoveryAmount: storeRecoveryAmount.length > 0 ? storeRecoveryAmount : undefined,
      storeDensity: storeDensity.length > 0 ? storeDensity : undefined,
      highwayProximity: highwayProximity.length > 0 ? highwayProximity : undefined,
    };

    if (onApplyFilters) {
      onApplyFilters(appliedFilters);
    }
  };

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <>
      {isOpen && <div className="sites-filter-overlay" onClick={onClose}></div>}
      <div className={`sites-filter-panel ${isOpen ? 'open' : ''}`}>
        <div className="sites-filter-header">
          <div className="header-left-group">
            <div className="header-icon">
              <HiOutlineFilter />
            </div>
            <div className="header-left-f">
              <h2>Filters</h2>
              <p className="header-subtitle">Apply filters to refine your stores</p>
            </div>
            {activeFilterCount > 0 && (
              <span className="active-filters-badge">{activeFilterCount}</span>
            )}
          </div>
          <button className="close-btn" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {hasActiveFilters && (
          <div className="clear-filters-section">
            <button className="clear-all-filters-btn" onClick={handleClearAllFilters}>
              <HiOutlineX />
              Clear All Filters
            </button>
          </div>
        )}

        <div className="sites-filter-content">
          <div className="filters-tab">
            {hasActiveFilters && (
              <div className="active-filters-display">
                <div className="active-filters-title">Active Filters</div>
                <div className="active-filters-list">
                  {status !== 'All Status' && (
                    <span className="active-filter-chip">
                      {status}
                      <button className="filter-chip-remove" onClick={() => setStatus('All Status')} aria-label="Remove Status filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {solutions.length > 0 && (
                    <span className="active-filter-chip">
                      Solutions: {solutions.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setSolutions([])} aria-label="Remove Solutions filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {whitespace.length > 0 && (
                    <span className="active-filter-chip">
                      Whitespace: {whitespace.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setWhitespace([])} aria-label="Remove Whitespace filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {subBrands.length > 0 && (
                    <span className="active-filter-chip">
                      Sub-brands: {subBrands.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setSubBrands([])} aria-label="Remove Sub-brands filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {states.length > 0 && (
                    <span className="active-filter-chip">
                      States: {states.join(', ')}
                      <button className="filter-chip-remove" onClick={() => handleStatesChange([])} aria-label="Remove States filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {counties.length > 0 && (
                    <span className="active-filter-chip">
                      Counties: {counties.join(', ')}
                      <button className="filter-chip-remove" onClick={() => handleCountiesChange([])} aria-label="Remove Counties filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {cities.length > 0 && (
                    <span className="active-filter-chip">
                      Cities: {cities.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setCities([])} aria-label="Remove Cities filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {locatedInShoppingComplex !== 'All' && (
                    <span className="active-filter-chip">
                      {locatedInShoppingComplex === 'Yes, with containment store(s) in the complex' ? 'Shopping Complex: With CM' : 'Shopping Complex: Without CM'}
                      <button className="filter-chip-remove" onClick={() => setLocatedInShoppingComplex('All')} aria-label="Remove Shopping Complex filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {crimeLevel.length > 0 && crimeLevel.map(cl => (
                    <span key={cl} className="active-filter-chip">
                      Crime: {cl.split(' ')[0]}
                      <button className="filter-chip-remove" onClick={() => setCrimeLevel(crimeLevel.filter(c => c !== cl))} aria-label={`Remove ${cl} crime level filter`}><HiOutlineX /></button>
                    </span>
                  ))}
                  {cartPenalty !== 'All' && (
                    <span className="active-filter-chip">
                      Cart Ordinance: {cartPenalty}
                      <button className="filter-chip-remove" onClick={() => setCartPenalty('All')} aria-label="Remove Cart Ordinance filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {isRangeActive(purchekNeighborhoodSites, 100) && (
                    <span className="active-filter-chip">
                      Purchek Sites: {purchekNeighborhoodSites.min}-{purchekNeighborhoodSites.max}
                      <button className="filter-chip-remove" onClick={() => setPurchekNeighborhoodSites({ min: 0, max: 100 })} aria-label="Remove Purchek Sites filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {isRangeActive(purchekPenetration, 100) && (
                    <span className="active-filter-chip">
                      Purchek Penetration: {purchekPenetration.min}-{purchekPenetration.max}%
                      <button className="filter-chip-remove" onClick={() => setPurchekPenetration({ min: 0, max: 100 })} aria-label="Remove Purchek Penetration filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {isRangeActive(containmentNeighborhoodSites, 100) && (
                    <span className="active-filter-chip">
                      Containment Sites: {containmentNeighborhoodSites.min}-{containmentNeighborhoodSites.max}
                      <button className="filter-chip-remove" onClick={() => setContainmentNeighborhoodSites({ min: 0, max: 100 })} aria-label="Remove Containment Sites filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {isRangeActive(containmentPenetration, 100) && (
                    <span className="active-filter-chip">
                      Containment Penetration: {containmentPenetration.min}-{containmentPenetration.max}%
                      <button className="filter-chip-remove" onClick={() => setContainmentPenetration({ min: 0, max: 100 })} aria-label="Remove Containment Penetration filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {isRangeActive(pairedWheelPenetration, 100) && (
                    <span className="active-filter-chip">
                      Paired Wheel: {pairedWheelPenetration.min}-{pairedWheelPenetration.max}%
                      <button className="filter-chip-remove" onClick={() => setPairedWheelPenetration({ min: 0, max: 100 })} aria-label="Remove Paired Wheel filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {isRangeActive(singleWheelReplacement, 100) && (
                    <span className="active-filter-chip">
                      Single Wheel: {singleWheelReplacement.min}-{singleWheelReplacement.max}%
                      <button className="filter-chip-remove" onClick={() => setSingleWheelReplacement({ min: 0, max: 100 })} aria-label="Remove Single Wheel filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {isRangeActive(hotDays, 365) && (
                    <span className="active-filter-chip">
                      Hot Days: {hotDays.min}-{hotDays.max}
                      <button className="filter-chip-remove" onClick={() => setHotDays({ min: 0, max: 365 })} aria-label="Remove Hot Days filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {isRangeActive(coldDays, 365) && (
                    <span className="active-filter-chip">
                      Cold Days: {coldDays.min}-{coldDays.max}
                      <button className="filter-chip-remove" onClick={() => setColdDays({ min: 0, max: 365 })} aria-label="Remove Cold Days filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {neighbourhoodPushoutIncidents.length > 0 && (
                    <span className="active-filter-chip">
                      Neighbourhood Pushout: {neighbourhoodPushoutIncidents.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setNeighbourhoodPushoutIncidents([])} aria-label="Remove Neighbourhood Pushout filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {storePushoutIncidents.length > 0 && (
                    <span className="active-filter-chip">
                      Store Pushout: {storePushoutIncidents.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setStorePushoutIncidents([])} aria-label="Remove Store Pushout filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {neighbourhoodRecoveryAmount.length > 0 && (
                    <span className="active-filter-chip">
                      Neighbourhood Recovery: {neighbourhoodRecoveryAmount.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setNeighbourhoodRecoveryAmount([])} aria-label="Remove Neighbourhood Recovery filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {storeRecoveryAmount.length > 0 && (
                    <span className="active-filter-chip">
                      Store Recovery: {storeRecoveryAmount.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setStoreRecoveryAmount([])} aria-label="Remove Store Recovery filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {storeDensity.length > 0 && (
                    <span className="active-filter-chip">
                      Store Density: {storeDensity.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setStoreDensity([])} aria-label="Remove Store Density filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {highwayProximity.length > 0 && (
                    <span className="active-filter-chip">
                      Highway Proximity: {highwayProximity.join(', ')}
                      <button className="filter-chip-remove" onClick={() => setHighwayProximity([])} aria-label="Remove Highway Proximity filter"><HiOutlineX /></button>
                    </span>
                  )}
                  {quickFilters.length > 0 && quickFilters.map(qf => {
                    const label = quickFilterOptions.find(opt => opt.id === qf)?.label || qf;
                    return (
                      <span key={qf} className="active-filter-chip">
                        {label}
                        <button className="filter-chip-remove" onClick={() => setQuickFilters(quickFilters.filter(q => q !== qf))} aria-label={`Remove ${label} quick filter`}><HiOutlineX /></button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Group 1: Store Status */}
            <div className="filter-group">
              <div className="filter-group-header">
                <HiOutlineUserGroup className="group-icon" />
                <span className="group-title">Store Status</span>
                {storeStatusCount > 0 && <span className="group-filter-count">{storeStatusCount}</span>}
              </div>
              <div className="filter-group-body">
                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Customer Status</h3>
                    {status !== 'All Status' && <span className="active-filter-tag">1</span>}
                  </div>
                  <div className="filter-fields">
                    <div className="filter-field">
                      <CustomDropdown value={status} options={statusOptions} onChange={setStatus} />
                    </div>
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Installed Solutions</h3>
                    {solutions.length > 0 && <span className="section-filter-count">{solutions.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={solutions}
                      options={solutionsOptions}
                      onChange={setSolutions}
                      placeholder="All solutions"
                      disabledOptions={whitespace}
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3 title={getTooltipText('whitespace')}>Whitespace Solutions</h3>
                    {whitespace.length > 0 && <span className="section-filter-count">{whitespace.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={whitespace}
                      options={whitespaceOptions}
                      onChange={setWhitespace}
                      placeholder="All solutions"
                      disabledOptions={solutions}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Group 2: Sub-brand (only for ultimate parent) */}
            {isUltimateParent && availableSubBrands.length > 0 && (
              <div className="filter-group">
                <div className="filter-group-header">
                  <HiOutlineTag className="group-icon" />
                  <span className="group-title">Sub-brand</span>
                  {subBrands.length > 0 && <span className="group-filter-count">{subBrands.length}</span>}
                </div>
                <div className="filter-group-body">
                  <div className="sf-filter-section">
                    <div className="section-header">
                      <h3>Brand Name</h3>
                      {subBrands.length > 0 && <span className="section-filter-count">{subBrands.length}</span>}
                    </div>
                    <div className="filter-fields">
                      <MultiSelectDropdown
                        values={subBrands}
                        options={availableSubBrands}
                        onChange={setSubBrands}
                        placeholder="All brands"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Group 3: Location */}
            <div className="filter-group">
              <div className="filter-group-header">
                <HiOutlineMapPin className="group-icon" />
                <span className="group-title">Location</span>
                {locationCount > 0 && <span className="group-filter-count">{locationCount}</span>}
              </div>
              <div className="filter-group-body">
                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>State</h3>
                    {states.length > 0 && <span className="section-filter-count">{states.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <div className="filter-field">
                      <MultiSelectDropdown
                        values={states}
                        options={availableStates}
                        onChange={handleStatesChange}
                        placeholder="All states"
                      />
                    </div>
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>County</h3>
                    {counties.length > 0 && <span className="section-filter-count">{counties.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <div className="filter-field">
                      <MultiSelectDropdown
                        values={counties}
                        options={filteredCounties}
                        onChange={handleCountiesChange}
                        placeholder="All counties"
                      />
                    </div>
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>City</h3>
                    {cities.length > 0 && <span className="section-filter-count">{cities.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <div className="filter-field">
                      <MultiSelectDropdown
                        values={cities}
                        options={filteredCities}
                        onChange={setCities}
                        placeholder="All cities"
                      />
                    </div>
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Located in a Shopping Complex</h3>
                    {locatedInShoppingComplex !== 'All' && <span className="active-filter-tag">1</span>}
                  </div>
                  <div className="filter-fields">
                    <CustomDropdown
                      value={locatedInShoppingComplex}
                      options={['All', 'Yes, with containment store(s) in the complex', 'Yes, without containment store(s) in the complex']}
                      onChange={setLocatedInShoppingComplex}
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Store Density</h3>
                    {storeDensity.length > 0 && <span className="section-filter-count">{storeDensity.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={storeDensity}
                      options={storeDensityOptions}
                      onChange={setStoreDensity}
                      placeholder="All densities"
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Highway Proximity</h3>
                    {highwayProximity.length > 0 && <span className="section-filter-count">{highwayProximity.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={highwayProximity}
                      options={highwayProximityOptions}
                      onChange={setHighwayProximity}
                      placeholder="All proximities"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Group 4: Risk & Regulatory */}
            <div className="filter-group">
              <div className="filter-group-header">
                <HiOutlineShieldCheck className="group-icon" />
                <span className="group-title">Risk & Regulatory</span>
                {riskCount > 0 && <span className="group-filter-count">{riskCount}</span>}
              </div>
              <div className="filter-group-body">
                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3 title={getTooltipText('crimeIndex')}>Crime Level</h3>
                    {crimeLevel.length > 0 && <span className="section-filter-count">{crimeLevel.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={crimeLevel}
                      options={crimeLevelOptions}
                      onChange={setCrimeLevel}
                      placeholder="All crime levels"
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3 title={getTooltipText('cartPenalty')}>Cart Abandonment Ordinance</h3>
                    {cartPenalty !== 'All' && <span className="active-filter-tag">1</span>}
                  </div>
                  <div className="filter-fields">
                    <CustomDropdown value={cartPenalty} options={cartAbandonmentOptions} onChange={setCartPenalty} />
                  </div>
                </div>
              </div>
            </div>

            {/* Group 5: Gatekeeper Account Penetration */}
            <div className="filter-group">
              <div className="filter-group-header">
                <HiOutlineChartBar className="group-icon" />
                <span className="group-title">Gatekeeper Account Penetration</span>
                {gkapCount > 0 && <span className="group-filter-count">{gkapCount}</span>}
              </div>
              <div className="filter-group-body">
                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Purchek Sites (Neighbourhood)</h3>
                    {isRangeActive(purchekNeighborhoodSites, 100) && <span className="section-filter-count">1</span>}
                  </div>
                  <div className="filter-fields">
                    <RangeSlider
                      min={purchekNeighborhoodSites.min}
                      max={purchekNeighborhoodSites.max}
                      minLimit={0}
                      maxLimit={100}
                      onChange={(min, max) => setPurchekNeighborhoodSites({ min, max })}
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Purchek Penetration %</h3>
                    {isRangeActive(purchekPenetration, 100) && <span className="section-filter-count">1</span>}
                  </div>
                  <div className="filter-fields">
                    <RangeSlider
                      min={purchekPenetration.min}
                      max={purchekPenetration.max}
                      minLimit={0}
                      maxLimit={100}
                      onChange={(min, max) => setPurchekPenetration({ min, max })}
                      suffix="%"
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Containment Sites (Neighbourhood)</h3>
                    {isRangeActive(containmentNeighborhoodSites, 100) && <span className="section-filter-count">1</span>}
                  </div>
                  <div className="filter-fields">
                    <RangeSlider
                      min={containmentNeighborhoodSites.min}
                      max={containmentNeighborhoodSites.max}
                      minLimit={0}
                      maxLimit={100}
                      onChange={(min, max) => setContainmentNeighborhoodSites({ min, max })}
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Containment Penetration %</h3>
                    {isRangeActive(containmentPenetration, 100) && <span className="section-filter-count">1</span>}
                  </div>
                  <div className="filter-fields">
                    <RangeSlider
                      min={containmentPenetration.min}
                      max={containmentPenetration.max}
                      minLimit={0}
                      maxLimit={100}
                      onChange={(min, max) => setContainmentPenetration({ min, max })}
                      suffix="%"
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Paired Wheel Penetration %</h3>
                    {isRangeActive(pairedWheelPenetration, 100) && <span className="section-filter-count">1</span>}
                  </div>
                  <div className="filter-fields">
                    <RangeSlider
                      min={pairedWheelPenetration.min}
                      max={pairedWheelPenetration.max}
                      minLimit={0}
                      maxLimit={100}
                      onChange={(min, max) => setPairedWheelPenetration({ min, max })}
                      suffix="%"
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Single Wheel Replacement %</h3>
                    {isRangeActive(singleWheelReplacement, 100) && <span className="section-filter-count">1</span>}
                  </div>
                  <div className="filter-fields">
                    <RangeSlider
                      min={singleWheelReplacement.min}
                      max={singleWheelReplacement.max}
                      minLimit={0}
                      maxLimit={100}
                      onChange={(min, max) => setSingleWheelReplacement({ min, max })}
                      suffix="%"
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* Group 6: Environmental Stress */}
            <div className="filter-group">
              <div className="filter-group-header">
                <HiOutlineSun className="group-icon" />
                <span className="group-title">Environmental Stress</span>
                {environmentalCount > 0 && <span className="group-filter-count">{environmentalCount}</span>}
              </div>
              <div className="filter-group-body">
                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Hot Days (Extreme Heat 90°F or higher)</h3>
                    {isRangeActive(hotDays, 365) && <span className="section-filter-count">1</span>}
                  </div>
                  <div className="filter-fields">
                    <RangeSlider
                      min={hotDays.min}
                      max={hotDays.max}
                      minLimit={0}
                      maxLimit={365}
                      onChange={(min, max) => setHotDays({ min, max })}
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Cold Days (Extreme Cold 32°F or lower)</h3>
                    {isRangeActive(coldDays, 365) && <span className="section-filter-count">1</span>}
                  </div>
                  <div className="filter-fields">
                    <RangeSlider
                      min={coldDays.min}
                      max={coldDays.max}
                      minLimit={0}
                      maxLimit={365}
                      onChange={(min, max) => setColdDays({ min, max })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Group 7: Pushout & Recovery */}
            <div className="filter-group">
              <div className="filter-group-header">
                <HiOutlineCurrencyDollar className="group-icon" />
                <span className="group-title">Pushout & Recovery</span>
                {pushoutRecoveryCount > 0 && <span className="group-filter-count">{pushoutRecoveryCount}</span>}
              </div>
              <div className="filter-group-body">
                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Neighbourhood Pushout Incidents Prevented</h3>
                    {neighbourhoodPushoutIncidents.length > 0 && <span className="section-filter-count">{neighbourhoodPushoutIncidents.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={neighbourhoodPushoutIncidents}
                      options={neighbourhoodPushoutOptions}
                      onChange={setNeighbourhoodPushoutIncidents}
                      placeholder="All ranges"
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Store Pushout Incidents Prevented</h3>
                    {storePushoutIncidents.length > 0 && <span className="section-filter-count">{storePushoutIncidents.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={storePushoutIncidents}
                      options={storePushoutOptions}
                      onChange={setStorePushoutIncidents}
                      placeholder="All ranges"
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Neighbourhood Recovery Amount ($)</h3>
                    {neighbourhoodRecoveryAmount.length > 0 && <span className="section-filter-count">{neighbourhoodRecoveryAmount.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={neighbourhoodRecoveryAmount}
                      options={neighbourhoodRecoveryOptions}
                      onChange={setNeighbourhoodRecoveryAmount}
                      placeholder="All ranges"
                    />
                  </div>
                </div>

                <div className="sf-filter-section">
                  <div className="section-header">
                    <h3>Store Recovery Amount ($)</h3>
                    {storeRecoveryAmount.length > 0 && <span className="section-filter-count">{storeRecoveryAmount.length}</span>}
                  </div>
                  <div className="filter-fields">
                    <MultiSelectDropdown
                      values={storeRecoveryAmount}
                      options={storeRecoveryOptions}
                      onChange={setStoreRecoveryAmount}
                      placeholder="All ranges"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Group 8: Opportunity Shortcuts */}
            <div className="filter-group">
              <div className="filter-group-header">
                <HiOutlineBolt className="group-icon" />
                <span className="group-title">Opportunity Shortcuts</span>
                {quickFilters.length > 0 && <span className="group-filter-count">{quickFilters.length}</span>}
              </div>
              <div className="filter-group-body">
                <div className="quick-filters-grid">
                  {quickFilterOptions.map((filter) => (
                    <button
                      key={filter.id}
                      className={`quick-filter-btn ${quickFilters.includes(filter.id) ? 'active' : ''}`}
                      onClick={() => handleQuickFilterToggle(filter.id)}
                    >
                      <span>{filter.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="filter-actions">
              <button className="close-panel-btn" onClick={onClose}>
                Close
              </button>
              <button className="apply-filters-btn" onClick={handleApplyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SitesFilters;
